import { useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../components/PageMeta';
import useReveal from '../hooks/useReveal';
import { COMPANY } from '../data/content';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialState = { name: '', email: '', company: '', subject: '', message: '', website: '' };

export default function Contact() {
  const { t } = useTranslation();
  useReveal();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [ok, setOk] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error(t('contact_page.form.validation_missing'));
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: form.website,
      });
      setOk(true);
      setForm(initialState);
      toast.success(t('contact_page.form.success_toast'));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : t('contact_page.form.generic_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageMeta pageKey="contact" />
      <Toaster theme="dark" position="top-center" />
      <section className="px-6 md:px-12 pt-16 md:pt-24 pb-16">
        <div className="max-w-7xl mx-auto">
          <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-8">{t('contact_page.eyebrow')}</p>
          <h1 className="reveal font-serif text-5xl md:text-7xl lg:text-8xl text-[#0B1F3F] leading-[1.02] tracking-tight max-w-5xl">
            {t('contact_page.title_pre')} <span className="italic">{t('contact_page.title_italic')}</span>{t('contact_page.title_post')}
          </h1>
        </div>
      </section>

      <section className="px-6 md:px-12 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-16 md:gap-24">
          <div className="md:col-span-5 space-y-12">
            <div>
              <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#6B5B4A] mb-4">{t('contact_page.correspondence')}</p>
              {COMPANY.addressLines.map((line) => (
                <p key={line} className="font-serif text-xl md:text-2xl text-[#0B1F3F] leading-snug">
                  {line}
                </p>
              ))}
            </div>
            <div>
              <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#6B5B4A] mb-4">{t('contact_page.investor_relations')}</p>
              <a href={`mailto:${COMPANY.contactEmail}`} data-testid="contact-email" className="block font-serif text-xl md:text-2xl text-[#0B1F3F] hover:text-[#B58F45] transition-colors">
                {COMPANY.contactEmail}
              </a>
              <a href={`tel:${COMPANY.contactPhoneRaw}`} data-testid="contact-phone" className="block font-serif text-xl md:text-2xl text-[#0B1F3F] hover:text-[#B58F45] transition-colors mt-2">
                {COMPANY.contactPhone}
              </a>
            </div>
            <div>
              <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#6B5B4A] mb-4">{t('contact_page.office_hours')}</p>
              <p className="text-[#0B1F3F] font-sans">{t('contact_page.hours_body')}</p>
              <p className="text-[#6B5B4A] font-sans text-sm mt-2">{t('contact_page.hours_note')}</p>
            </div>
          </div>

          <div className="md:col-span-7">
            {ok ? (
              <div data-testid="contact-success" className="border border-[#B58F45]/40 bg-[#EBE3D0] p-10 md:p-14">
                <p className="reveal text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-6">{t('contact_page.success.eyebrow')}</p>
                <h2 className="reveal font-serif text-3xl md:text-4xl text-[#0B1F3F] leading-tight mb-6">
                  {t('contact_page.success.title')}
                </h2>
                <p className="text-[#6B5B4A] leading-relaxed mb-8">
                  {t('contact_page.success.body_pre')}{' '}
                  <a className="text-[#0B1F3F] underline decoration-[#B58F45] underline-offset-4" href={`mailto:${COMPANY.contactEmail}`}>{COMPANY.contactEmail}</a>.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another"
                  onClick={() => setOk(false)}
                  className="text-xs tracking-[0.25em] uppercase text-[#0B1F3F] border-b border-[#0B1F3F]/25 pb-1 hover:text-[#B58F45] hover:border-[#B58F45] transition-colors"
                >
                  {t('contact_page.success.cta_another')}
                </button>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-8" data-testid="contact-form">
                {/* Honeypot field for bot detection — hidden from human users */}
                <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}>
                  <label>
                    Website
                    <input
                      type="text"
                      name="website"
                      tabIndex="-1"
                      autoComplete="off"
                      value={form.website}
                      onChange={update('website')}
                    />
                  </label>
                </div>
                <FormField label={t('contact_page.form.name')} required>
                  <input
                    data-testid="contact-input-name"
                    type="text"
                    value={form.name}
                    onChange={update('name')}
                    required
                    className="w-full bg-transparent border-b border-[#0B1F3F]/25 py-4 text-[#0B1F3F] focus:border-[#B58F45] transition-all outline-none font-sans placeholder-white/30 text-base"
                    placeholder={t('contact_page.form.name_placeholder')}
                  />
                </FormField>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <FormField label={t('contact_page.form.email')} required>
                    <input
                      data-testid="contact-input-email"
                      type="email"
                      value={form.email}
                      onChange={update('email')}
                      required
                      className="w-full bg-transparent border-b border-[#0B1F3F]/25 py-4 text-[#0B1F3F] focus:border-[#B58F45] transition-all outline-none font-sans placeholder-white/30 text-base"
                      placeholder={t('contact_page.form.email_placeholder')}
                    />
                  </FormField>
                  <FormField label={t('contact_page.form.company')}>
                    <input
                      data-testid="contact-input-company"
                      type="text"
                      value={form.company}
                      onChange={update('company')}
                      className="w-full bg-transparent border-b border-[#0B1F3F]/25 py-4 text-[#0B1F3F] focus:border-[#B58F45] transition-all outline-none font-sans placeholder-white/30 text-base"
                      placeholder={t('contact_page.form.company_placeholder')}
                    />
                  </FormField>
                </div>

                <FormField label={t('contact_page.form.subject')} required>
                  <input
                    data-testid="contact-input-subject"
                    type="text"
                    value={form.subject}
                    onChange={update('subject')}
                    required
                    className="w-full bg-transparent border-b border-[#0B1F3F]/25 py-4 text-[#0B1F3F] focus:border-[#B58F45] transition-all outline-none font-sans placeholder-white/30 text-base"
                    placeholder={t('contact_page.form.subject_placeholder')}
                  />
                </FormField>

                <FormField label={t('contact_page.form.message')} required>
                  <textarea
                    data-testid="contact-input-message"
                    value={form.message}
                    onChange={update('message')}
                    required
                    rows={5}
                    className="w-full bg-transparent border-b border-[#0B1F3F]/25 py-4 text-[#0B1F3F] focus:border-[#B58F45] transition-all outline-none font-sans placeholder-white/30 text-base resize-none"
                    placeholder={t('contact_page.form.message_placeholder')}
                  />
                </FormField>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center px-10 py-5 bg-[#0B1F3F] text-[#F5EFE1] hover:bg-[#B58F45] hover:text-[#0B1F3F] disabled:opacity-60 disabled:cursor-not-allowed font-sans text-xs tracking-[0.25em] uppercase transition-colors duration-300 mt-4"
                >
                  {submitting ? (
                    <><Loader2 size={16} className="mr-3 animate-spin" /> {t('contact_page.form.sending')}</>
                  ) : (
                    <>{t('contact_page.form.submit')} <ArrowRight size={14} className="ml-3" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] tracking-[0.3em] uppercase text-[#6B5B4A] mb-2">
        {label}{required && <span className="text-[#B58F45] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
